<template>
  <div class="success-page">
    <PaymentLoading v-if="isLoading" />

    <OrderResult
      v-else-if="isConfirmed"
      image-url="/img/order/usimsa-ta-order-success.png"
      image-title="주문완료!"
      hide-main-button
      sub-button-text="주문상세보기"
      :sub-button-to="detailPath"
      :shuttle-order-no="completeInfo?.reservationId"
    >
      <template
        v-if="completeInfo"
        #order-detail
      >
        <div>
          <p class="tw:typo-title-xs tw:font-bold">유심사 TA</p>
          <p class="tw:mt-1 tw:typo-text-m tw:font-semibold">{{ routeTitle }}</p>
        </div>

        <div class="tw:mt-2!">
          <div
            v-for="(leg, idx) in completeInfo.legs"
            :key="idx"
            class="tw:flex tw:typo-text-m"
            :class="idx > 0 ? 'tw:mt-2!' : ''"
          >
            <p class="tw:w-[80px] tw:shrink-0 tw:font-semibold tw:text-usdg-color-gray-600">
              {{ legLabel(leg) }}
            </p>
            <p>{{ leg.originAddress }} → {{ leg.destAddress }}</p>
          </div>
        </div>

        <div class="tw:border-b tw:border-b-[#E5E5EC] tw:mt-3!"></div>

        <div class="tw:mt-4!">
          <div class="tw:flex tw:typo-text-m">
            <p class="tw:w-[80px] tw:text-usdg-color-gray-600">주문번호</p>
            <p>{{ completeInfo.orderNo }}</p>
          </div>
          <div class="tw:flex tw:typo-text-m tw:mt-2!">
            <p class="tw:w-[80px] tw:text-usdg-color-gray-600">인원 · 수하물</p>
            <p>인원 {{ completeInfo.passengers }}명 · 수하물 {{ completeInfo.luggage }}개</p>
          </div>
          <div class="tw:flex tw:typo-text-m tw:mt-2!">
            <p class="tw:w-[80px] tw:text-usdg-color-gray-600">결제금액</p>
            <p>{{ commaWon(completeInfo.totalAmount) }}</p>
          </div>
        </div>
      </template>
    </OrderResult>

    <Modal
      :visibility="isShowFailModal"
      :title="failTitle"
      width="296px"
      @click-main-button="handleFailConfirm"
    />
  </div>
</template>

<script setup lang="ts">
definePageMeta({ layout: 'center' })

const { approveReservation, getReservationComplete } = useAirportTaxiAPI()
const { replacePrevUrl } = useOrderResult()
const { commaWon } = useUi()
const { query } = useRoute()

const { orderId, paymentKey, amount } = query as Record<string, string>

const APPROVE_RETRY_INTERVAL_MS = 2000
const APPROVE_RETRY_LIMIT_MS = 60000

const storageKey = `airportTaxiApprove:${orderId}`
let clearKeyOnConfirm = false

const isLoading = ref(true)
const isConfirmed = ref(false)
const isShowFailModal = ref(false)
const failTitle = ref('')
const completeInfo = ref<AirportTaxiCompleteInfo | null>(null)

const detailPath = computed(() =>
  completeInfo.value ? `/user/history/airport-taxi/${completeInfo.value.reservationId}` : undefined
)

const showFail = (title: string) => {
  isLoading.value = false
  failTitle.value = title
  isShowFailModal.value = true
}

const handleFailConfirm = () => {
  // 확정 실패 후 사용자가 떠날 때만 키 삭제 (새로고침 시에는 같은 키 유지)
  if (clearKeyOnConfirm) {
    try {
      sessionStorage.removeItem(storageKey)
    } catch {}
  }
  isShowFailModal.value = false
  replacePrevUrl()
}

// 왕복이 아니면 방향과 무관하게 '가는편'으로 통일 표시 (호텔↔호텔 편도 포함)
const legLabel = (leg: AirportTaxiCompleteLeg) => {
  if (completeInfo.value?.tripType !== 'ROUND_TRIP') return '가는편'
  return leg.direction === 'AIRPORT_TO_DEST' ? '가는편' : '오는편'
}

const routeTitle = computed(() => {
  if (!completeInfo.value) return ''
  if (completeInfo.value.tripType === 'ROUND_TRIP') return '공항 ↔ 호텔'
  const firstLeg = completeInfo.value.legs[0]
  if (firstLeg?.direction === 'HTL_TO_HTL') return '호텔 → 호텔'
  return firstLeg?.direction === 'DEST_TO_AIRPORT' ? '호텔 → 공항' : '공항 → 호텔'
})

onMounted(async () => {
  if (!orderId || !amount) {
    showFail('결제 정보가 올바르지 않습니다')
    return
  }

  const parsedAmount = parseFloat(amount)
  if (isNaN(parsedAmount)) {
    showFail('결제 금액이 올바르지 않습니다')
    return
  }

  const payload: ApproveAirportTaxiPayload = {
    orderNo: orderId,
    amount: parsedAmount,
    ...(paymentKey ? { paymentKey } : {})
  }

  // 같은 orderNo + paymentKey 재시도(새로고침 등)는 같은 멱등키, paymentKey가 바뀌면 새로 생성
  let idempotencyKey = ''
  try {
    const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null')
    if (saved?.paymentKey === (paymentKey ?? null)) idempotencyKey = saved.key
  } catch {}
  if (!idempotencyKey) {
    idempotencyKey = crypto.randomUUID()
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({ paymentKey: paymentKey ?? null, key: idempotencyKey })
      )
    } catch {}
  }

  // 재시도 대상: ① 응답 자체가 없는 경우(네트워크 오류/timeout) ② 409 중 '처리 중' 메시지
  // 같은 멱등키로 다른 요청을 보낸 409는 재시도하지 않고 실패 모달
  const isRetryable = (e: any) => {
    const hasResponse = !!(e && typeof e === 'object' && (e.statusCode || e.error))
    if (!hasResponse) return true
    return (
      (e.statusCode ?? e.error?.statusCode) === 409 &&
      String(e.error?.message ?? e.message ?? '').includes('처리 중')
    )
  }
  const deadline = Date.now() + APPROVE_RETRY_LIMIT_MS

  try {
    let res: Awaited<ReturnType<typeof approveReservation>>
    while (true) {
      try {
        res = await approveReservation(payload, idempotencyKey)
        break
      } catch (e: any) {
        if (!isRetryable(e)) throw e
        if (Date.now() + APPROVE_RETRY_INTERVAL_MS >= deadline) {
          showFail('예약 처리 결과를 확인 중입니다. 구매내역에서 확인하시거나 고객센터로 문의해주세요.')
          return
        }
        await new Promise((resolve) => setTimeout(resolve, APPROVE_RETRY_INTERVAL_MS))
      }
    }
    isLoading.value = false

    if ('success' in res && res.success && res.data?.statusCode === 'CONFIRMED') {
      isConfirmed.value = true

      try {
        const completeRes = await getReservationComplete(res.data.reservationId)
        if ('success' in completeRes && completeRes.success) {
          completeInfo.value = completeRes.data
        }
      } catch (e) {
        console.error('결제 완료 요약 조회 실패:', e)
      }

      // approve 응답의 leg 위치 정보가 비어있어 completeInfo의 legs(주소 신뢰 가능)로 보강해서 트래킹
      useAirbridge().trackAirportTaxiOrderCompleted(res.data, completeInfo.value?.legs)
    } else {
      // BOOKING_FAILED: 백엔드가 자동 환불 처리
      showFail('예약에 실패했어요. 결제하신 금액은 자동 환불됩니다.')
    }
  } catch (e: any) {
    // 4xx 확정 실패는 같은 키로 재호출해도 같은 실패만 반환되므로 키를 삭제해 새로 시작하게 함
    // 단, 409 '이전 결과를 확인할 수 없습니다'는 승인이 이미 끝난 상태라 키를 유지(중복 승인 방지)
    const status = e?.statusCode ?? e?.error?.statusCode
    const message = String(e?.error?.message ?? '')
    if (status >= 400 && status < 500 && !message.includes('이전 예약 승인 결과')) {
      clearKeyOnConfirm = true
    }
    showFail(e?.error?.message || '결제 승인 중 오류가 발생했습니다')
  }
})
</script>

<style scoped lang="scss">
.success-page {
  position: relative;
  min-height: 100dvh;
}
</style>
